import React from 'react';
import { NJToastContainer, NJButton } from '@engie-group/fluid-design-system-react';
import { useState, useEffect, useCallback } from 'react';
import { Flex, Table } from "antd";
import './AdminAnalyze.css';
import { apiGatewayConfig } from '../../../config';
import { displayNetworkError, invokeLambda } from '../../../api';
import { useOktaAuth } from "@okta/okta-react";
import Loading from '../../../Loading';
import InsightsPopup from '../../../InsightsPopup';

const AdminAnalyze = ({ active }) => {
    const { oktaAuth } = useOktaAuth();

    const defaultJobDescription = 'This is a default job description.';

    const [isLoading, setIsLoading] = useState(false);
    const [isOpenInsightsPopup, setIsOpenInsightsPopup] = useState(false);
    const [options, setOptions] = useState({});
    const [response, setResponse] = useState(null);
    const [paramsSource, setParamsSource] = useState([]);
    const [expectationsSource, setExpectationsSource] = useState([]);
    const [jobDescription, setJobDescription] = useState(defaultJobDescription);

    const handleGenerateJobAd = useCallback(async (options, jobDescription) => {
        // Prepare request
        const request = {
            job_description: jobDescription || defaultJobDescription,
            persona: options.persona,
            profile: options.profile,
            seniority: options.seniority,
            globalEmployeeLevel: options.spc,
            experience: options.experience,
            job_family: options.jobFamily,
            tone: options.tone,
            gbu: options.gbu,
            countries: options.country,
            remote: options.remote,
            salary_range: options.salaryRange,
            currency: options.currency,
            language: options.language,
            woman_recruitment: !!options.woman,
            analysis: true
        }
        setIsLoading(true);
        // Call Lambda

        try {
            const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdGeneration, request);
            setIsLoading(false);
            setResponse(response);
            buildResponseTable(response);
        } catch (error) {
            console.error('Error invoking Lambda:', error);
            displayNetworkError(error);
            setIsLoading(false);
        }
    }, [oktaAuth]);

    const handleInsightsChange = useCallback((newOptions, newJobDescription) => {
        setIsOpenInsightsPopup(false);
        setOptions(newOptions);
        setJobDescription(newJobDescription);
        handleGenerateJobAd(newOptions, newJobDescription);
    }, [handleGenerateJobAd]);

    const createJobAd = () => {
        //setOptions({})
        setIsOpenInsightsPopup(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const resetOptions = useCallback(async (options) => {
        setOptions({});
        handleInsightsChange({}, jobDescription || defaultJobDescription);
    }, [handleInsightsChange]);

    useEffect(() => {
        if (active) {
            resetOptions();
            setJobDescription(defaultJobDescription);
        }
    }, [active, resetOptions]);





    function buildResponseTable(response) {
        // Params
        setParamsSource([
            { key: 'experience', value: response.params.experience },
            { key: 'global_employee_level', value: response.params.global_employee_level },
            { key: 'job_family', value: response.params.job_family },
            { key: 'language', value: response.params.language },
            { key: 'persona', value: response.params.persona },
            { key: 'target_gbu', value: response.params.target_gbu },
            { key: 'woman_recruitment', value: response.params.woman_recruitment ? 'Yes' : 'No' },
            {
                key: 'country', value: Array.isArray(response.params.country) ?
                    response.params.country.join(', ') : response.params.country
            },
        ]);

        // Expectations
        setExpectationsSource(
            response.raw_expectation.map((expectation, index) => ({
                key: index,
                Country: expectation.Country,
                Gender: expectation.Gender,
                'Global Employee Level': expectation['Global Employee Level'],
                'Job Family': expectation['Job Family'],
                Persona: expectation.Persona,
                Expectation: [
                    expectation['Expectation 1'],
                    expectation['Expectation 2'],
                    expectation['Expectation 3'],
                    expectation['Expectation 4'],
                    expectation['Expectation 5'],
                ].join('<br />'),
            }
            )));
    }

    const paramsColumns = [
        {
            title: 'Variable name',
            dataIndex: 'key',
            key: 'key',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
        },
    ];

    const expectationsColumns = [
        {
            title: 'Country',
            dataIndex: 'Country',
            key: 'Country',
        },
        {
            title: 'Gender',
            dataIndex: 'Gender',
            key: 'Gender',
        },
        {
            title: 'Global Employee Level',
            dataIndex: 'Global Employee Level',
            key: 'Global Employee Level',
        },
        {
            title: 'Job Family',
            dataIndex: 'Job Family',
            key: 'Job Family',
        },
        {
            title: 'Persona',
            dataIndex: 'Persona',
            key: 'Persona',
        },
        {
            title: 'Expectation',
            dataIndex: 'Expectation',
            key: 'Expectation',
            render: (text) => <div dangerouslySetInnerHTML={{ __html: text }} />
        },
    ];

    return (
        <>
            {isLoading && <Loading text="Loading..." />}
            <NJToastContainer type="full-width" />

            <InsightsPopup
                isOpen={isOpenInsightsPopup}
                handleClose={() => setIsOpenInsightsPopup(false)}
                handleValidate={handleInsightsChange}
                insightsOptions={options}
                jobDescription={jobDescription}
                editJobDescription={true}
            />

            <div className='analysis-data-content'>


                <Flex justify='left' align='center' gap={16} className='analysis-data-buttons-flex'>
                    <NJButton
                        label="Update Filters"
                        disabled={false}
                        onClick={() => {
                            createJobAd();
                        }}
                    />
                    <NJButton
                        label="Reset Filters"
                        variant='secondary'
                        disabled={false}
                        onClick={() => {
                            resetOptions();
                        }}
                    />
                </Flex>

                <div className='analysis-data-grey-box'>
                    <div className='analysis-data-table'>

                        <h3>Prompt expectations</h3>
                        {response && (
                            <div dangerouslySetInnerHTML={{
                                __html: response.profile_expectation.replace(/\n/g, '<br />')
                            }} />
                        )}

                        <h3>EVP</h3>
                        {response && (
                            <div dangerouslySetInnerHTML={{
                                __html: response.evp.replace(/\n/g, '<br />')
                            }} />
                        )}

                        <h3>Prompt</h3>
                        {response && (
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'inherit',
                                margin: 0
                            }}>
                                {response.prompt}
                            </pre>
                        )}

                        <br />

                        <h4>Expectations from database</h4>
                        <Table dataSource={expectationsSource} columns={expectationsColumns} size='small' pagination={false} />

                        <h4>Function parameters</h4>
                        <Table dataSource={paramsSource} columns={paramsColumns} size='small' pagination={false} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminAnalyze;
